<template>
  <div>
    <section v-if="contentReady" class="notes-hero light-hero">
      <div class="notes-hero-copy">
        <span class="notes-eyebrow">{{ intro.eyebrow }}</span>
        <h1>{{ intro.title }}</h1>
        <p v-if="intro.description">{{ intro.description }}</p>
        <div v-if="intro.tags.length" class="notes-hero-meta">
          <span v-for="tag in intro.tags" :key="tag">{{ tag }}</span>
        </div>
      </div>

      <div class="notes-hero-panel">
        <div class="notes-hero-stat">
          <strong>{{ paddedCount(totalVideos) }}</strong>
          <span>个视频</span>
        </div>
        <div class="notes-hero-stat">
          <strong>{{ paddedCount(totalCategories) }}</strong>
          <span>个分类</span>
        </div>
        <div class="notes-hero-stat">
          <strong>{{ paddedCount(embeddableVideos) }}</strong>
          <span>可内嵌</span>
        </div>
      </div>
    </section>

    <section v-if="contentReady" class="notes-toolbar">
      <div class="notes-tags">
        <button
          type="button"
          :class="{ 'is-active': currentFilter === 'all' }"
          @click="setFilter('all')"
        >
          全部
        </button>
        <button
          v-for="category in categories"
          :key="category.slug"
          type="button"
          :class="{ 'is-active': currentFilter === category.slug }"
          @click="setFilter(category.slug)"
        >
          {{ category.title }}
        </button>
      </div>

      <RouterLink
        class="notes-toolbar-link"
        :to="sampleVideo ? `/light-videos/${sampleVideo.slug}` : '/light-videos'"
      >
        {{ sampleVideo ? "播放最新视频" : "等待视频上线" }}
      </RouterLink>
    </section>

    <section class="notes-list light-video-list">
      <div v-if="!contentReady" class="notes-empty-state">
        <h2>内容加载中</h2>
        <p>正在从本地后端同步光影音域内容，请稍等一下。</p>
      </div>

      <div v-else-if="!filteredVideos.length" class="notes-empty-state">
        <h2>这里还没有视频</h2>
        <p>进入后台新增 B 站视频链接并保存后，这里就会显示你的作品列表。</p>
      </div>

      <article v-else v-for="item in pagedVideos" :key="item.slug" class="note-row-card light-video-card">
        <div class="note-row-cover light-video-cover">
          <iframe
            v-if="getVideoEmbedUrl(item)"
            class="light-video-list-embed"
            :src="getVideoEmbedUrl(item)"
            :title="item.title"
            loading="lazy"
            allow="fullscreen; autoplay; encrypted-media; picture-in-picture"
            allowfullscreen
          ></iframe>
          <div v-else class="light-video-unavailable">
            <strong>等待视频链接</strong>
            <span>填入 B 站链接后这里会直接播放。</span>
          </div>
        </div>

        <div class="note-row-body">
          <div class="note-row-meta">
            <span>{{ item.categoryTitle }}</span>
            <span>{{ item.date }}</span>
          </div>
          <h2>
            <RouterLink :to="`/light-videos/${item.slug}`">
              {{ item.title }}
            </RouterLink>
          </h2>
          <p v-if="getListSummary(item)" class="light-video-list-summary">
            {{ getListSummary(item) }}
          </p>
          <div class="note-row-footer">
            <span class="note-row-reading">{{ item.duration || "视频作品" }}</span>
            <RouterLink class="note-row-link" :to="`/light-videos/${item.slug}`">
              进入播放页
            </RouterLink>
          </div>
        </div>
      </article>
    </section>

    <nav v-if="filteredVideos.length" class="notes-pagination" aria-label="光影音域分页">
      <div class="notes-page-summary">
        第 {{ currentPage }} / {{ totalPages }} 页，共 {{ filteredVideos.length }} 个视频
      </div>
      <div class="notes-page-actions">
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === 1"
          @click="setPage(1)"
        >
          第一页
        </button>
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === 1"
          @click="setPage(currentPage - 1)"
        >
          上一页
        </button>
        <button
          v-for="page in totalPages"
          :key="page"
          type="button"
          class="notes-page-number"
          :class="{ 'is-active': page === currentPage }"
          @click="setPage(page)"
        >
          {{ page }}
        </button>
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === totalPages"
          @click="setPage(currentPage + 1)"
        >
          下一页
        </button>
        <button
          type="button"
          class="notes-page-button"
          :disabled="currentPage === totalPages"
          @click="setPage(totalPages)"
        >
          最后一页
        </button>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { computed, ref, watch, watchEffect } from "vue";
import { RouterLink } from "vue-router";
import { useContentStore } from "@/store/contentStore";

const videosPerPage = 10;
const currentPage = ref(1);
const currentFilter = ref("all");
const { lightVideos, moduleIntros, contentReady, getBilibiliEmbedUrl } = useContentStore();

const intro = computed(() => moduleIntros.value.lightVideos);

const categories = computed(() =>
  Array.from(
    new Map(
      lightVideos.value.map((item) => [
        item.categorySlug,
        { slug: item.categorySlug, title: item.categoryTitle },
      ])
    ).values()
  )
);

const filteredVideos = computed(() => {
  if (currentFilter.value === "all") {
    return lightVideos.value;
  }

  return lightVideos.value.filter((item) => item.categorySlug === currentFilter.value);
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredVideos.value.length / videosPerPage))
);

const pagedVideos = computed(() => {
  const startIndex = (currentPage.value - 1) * videosPerPage;
  return filteredVideos.value.slice(startIndex, startIndex + videosPerPage);
});

const totalVideos = computed(() => lightVideos.value.length);
const totalCategories = computed(() => categories.value.length);
const embeddableVideos = computed(
  () => lightVideos.value.filter((video) => getBilibiliEmbedUrl(video.sourceUrl, video)).length
);
const sampleVideo = computed(() => lightVideos.value[0] || null);

const getVideoEmbedUrl = (video) =>
  getBilibiliEmbedUrl(video?.sourceUrl, {
    page: video?.page,
  });

const getListSummary = (video) => video?.heroSummary || video?.summary || "";

const paddedCount = (value) => String(value).padStart(2, "0");

const setFilter = (filter) => {
  currentFilter.value = filter;
  currentPage.value = 1;
};

const setPage = (page) => {
  currentPage.value = Math.min(totalPages.value, Math.max(1, page));
};

watch(filteredVideos, () => {
  if (currentPage.value > totalPages.value) {
    currentPage.value = 1;
  }
});

watchEffect(() => {
  document.title = "圣域空间 | 光影音域";
});
</script>
